/**
 * VidROI Home Dashboard — the post-login landing page.
 *
 * Purpose: orient the user (especially on first sign-in) and give them a
 * single place to see "where am I in the setup, and what should I do next?"
 *
 * Sections:
 *   1. Welcome header with user's first name
 *   2. 5-step setup checklist with live completion detection
 *      (each step turns green + shows ✓ when done, click jumps to that tab)
 *   3. Demo video card (reuses the marketing-page video modal)
 *   4. Ask AI quick-prompts card (jumps into the AI tab pre-filled)
 *
 * Completion detection is best-effort: we use whatever signals the parent
 * Dashboard already loads (channels count, offers count, UTM settings,
 * video injection status). For "tracking pixel installed" we check whether
 * any tracking events have been recorded in the lifetime of the account.
 */
import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Youtube, Tag, ShieldCheck, Link2, Send, Check, ChevronRight,
  Play, Sparkles, X, ArrowRight,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/** Lightweight demo-video modal — placeholder copy until you record one.
 *  Mirrors the modal on the marketing page so we have a single voice. */
function DemoVideoModal({ onClose }) {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div
      className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="home-demo-modal"
    >
      <div
        className="relative w-full max-w-3xl bg-zinc-950 border border-white/10 rounded-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 z-10 w-8 h-8 rounded-full bg-black/60 hover:bg-black/90 text-white flex items-center justify-center"
          data-testid="home-demo-modal-close"
        >
          <X className="w-4 h-4" />
        </button>
        {/* Swap this <div> for a YouTube iframe when the demo video is ready. */}
        <div className="aspect-video bg-gradient-to-br from-zinc-900 to-black flex flex-col items-center justify-center text-center px-8">
          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-amber-500 to-red-500 flex items-center justify-center mb-5 shadow-[0_0_40px_-5px_rgba(255,80,0,0.6)]">
            <Play className="w-9 h-9 text-white ml-1" />
          </div>
          <h3 className="text-2xl font-semibold text-white mb-2">Demo video coming soon</h3>
          <p className="text-zinc-400 max-w-md leading-relaxed">
            A 2-minute walkthrough showing how to set up tracking and see your
            first attributed sale. In the meantime, the 5-step checklist below
            takes most creators &lt;10 min.
          </p>
        </div>
      </div>
    </div>
  );
}

const STARTER_PROMPTS = [
  "Which video drove the most revenue this month?",
  "What's my best-performing offer?",
  "What should I make a video about next?",
];

export default function DashboardHome({ user, channels, videos, onNavigate }) {
  const [videoOpen, setVideoOpen] = useState(false);
  const [hasTracking, setHasTracking] = useState(null);
  const [offersCount, setOffersCount] = useState(0);

  // Fetch the data this widget needs that isn't already lifted to App.js.
  // Best-effort — if any call fails the corresponding step just stays
  // unchecked, no error toasts (this is a passive overview surface).
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/tracking/pixel-status`);
        if (alive) setHasTracking(!!data?.installed);
      } catch {
        if (alive) setHasTracking(false);
      }
      try {
        const { data } = await axios.get(`${API}/offers`);
        if (alive) setOffersCount((data?.offers || data || []).length || 0);
      } catch {
        // leave at 0
      }
    })();
    return () => { alive = false; };
  }, []);

  const channelsCount = (channels || []).length;
  // "Build UTMs" = user has saved UTM settings (key in localStorage under their user id)
  const hasUtmSettings = (() => {
    try {
      if (!user?.user_id) return false;
      const raw = localStorage.getItem(`utm_builder_settings_v1:${user.user_id}`);
      return raw && raw !== "null";
    } catch { return false; }
  })();
  // "Inject short links" — any video with an injection_status set
  const hasInjected = (videos || []).some((v) => v.injection_status === "injected");

  const steps = [
    { id: "channel",  label: "Connect Channel",       hint: "OAuth into YouTube so VidROI can read your videos.",                 done: channelsCount > 0,  tab: "channels", icon: Youtube },
    { id: "offer",    label: "Create an Offer",        hint: "Tell VidROI what product / course / link to track.",                done: offersCount > 0,    tab: "offers",   icon: Tag },
    { id: "pixel",    label: "Add tracking code to site", hint: "Drop the JS pixel on your checkout page (do this BEFORE step 5).", done: hasTracking === true, tab: "tracking", icon: ShieldCheck },
    { id: "utm",      label: "Build UTMs",             hint: "Configure the UTM template — all videos use it automatically.",      done: !!hasUtmSettings,   tab: "utm",      icon: Link2 },
    { id: "inject",   label: "Inject short links",      hint: "Add tagged links into every video description, in bulk.",            done: hasInjected,        tab: "tracking", icon: Send },
  ];
  const doneCount = steps.filter((s) => s.done).length;
  const allDone = doneCount === steps.length;
  const firstName = (user?.name || "").split(" ")[0] || "there";

  const go = (tab) => onNavigate?.(tab);

  return (
    <div className="max-w-5xl mx-auto" data-testid="home-dashboard">
      {/* Header */}
      <header className="mb-7">
        <div className="text-[11px] uppercase tracking-widest text-amber-300 font-semibold mb-1.5">Welcome back</div>
        <h1 className="text-3xl sm:text-4xl font-semibold text-white">Hi {firstName} — let's get you set up.</h1>
        <p className="text-zinc-400 mt-2">
          {allDone
            ? "Setup is complete. Head to Analytics to see your data."
            : `${doneCount} of ${steps.length} steps done. Most creators finish in under 10 minutes.`}
        </p>
      </header>

      {/* Setup checklist */}
      <section className="mb-8" data-testid="home-setup-checklist">
        <div className="flex items-end justify-between mb-3">
          <h2 className="text-lg font-semibold text-white">Setup checklist</h2>
          <span className="text-xs text-zinc-500 font-mono">{doneCount} / {steps.length}</span>
        </div>
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden">
          {steps.map((s, idx) => {
            const Icon = s.icon;
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => go(s.tab)}
                className={`w-full flex items-center gap-4 px-4 py-3.5 text-left transition-colors group ${idx > 0 ? "border-t border-white/5" : ""} ${s.done ? "bg-emerald-500/[0.04] hover:bg-emerald-500/[0.08]" : "hover:bg-white/[0.04]"}`}
                data-testid={`home-step-${s.id}`}
              >
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                  s.done
                    ? "bg-emerald-500/15 border border-emerald-500/40 text-emerald-300"
                    : "bg-white/[0.04] border border-white/10 text-zinc-400"
                }`}>
                  {s.done ? <Check className="w-5 h-5" strokeWidth={3} /> : <Icon className="w-5 h-5" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className={`text-[11px] font-mono ${s.done ? "text-emerald-400" : "text-zinc-500"}`}>STEP {idx + 1}</span>
                    {s.done && <span className="text-[10px] uppercase tracking-widest text-emerald-400 font-semibold">Done</span>}
                  </div>
                  <div className={`font-semibold ${s.done ? "text-emerald-100" : "text-white"}`}>{s.label}</div>
                  <div className="text-sm text-zinc-400 mt-0.5">{s.hint}</div>
                </div>
                <ChevronRight className={`w-5 h-5 flex-shrink-0 transition-transform group-hover:translate-x-0.5 ${s.done ? "text-emerald-400/60" : "text-zinc-500"}`} />
              </button>
            );
          })}
        </div>
      </section>

      {/* Demo + Ask AI side-by-side */}
      <section className="grid md:grid-cols-2 gap-4">
        <button
          type="button"
          onClick={() => setVideoOpen(true)}
          className="text-left rounded-2xl border border-white/10 bg-gradient-to-br from-amber-500/[0.06] to-transparent p-5 hover:border-amber-500/30 transition-colors group"
          data-testid="home-demo-card"
        >
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-red-500 flex items-center justify-center shadow-[0_0_24px_-4px_rgba(255,80,0,0.5)] group-hover:scale-105 transition-transform">
              <Play className="w-4 h-4 text-white ml-0.5" />
            </div>
            <div>
              <div className="text-white font-semibold">Watch the 2-min demo</div>
              <div className="text-xs text-zinc-400">See VidROI attribute a sale end-to-end.</div>
            </div>
          </div>
          <div className="aspect-video rounded-lg bg-black/60 border border-white/5 flex items-center justify-center">
            <Play className="w-10 h-10 text-zinc-700 group-hover:text-amber-300 transition-colors" />
          </div>
        </button>

        <div
          className="rounded-2xl border border-white/10 bg-gradient-to-br from-red-500/[0.06] to-transparent p-5"
          data-testid="home-ask-ai-card"
        >
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-red-500 to-amber-500 flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-white" />
            </div>
            <div>
              <div className="text-white font-semibold">Ask AI about your data</div>
              <div className="text-xs text-zinc-400">Claude reads your videos + sales and answers.</div>
            </div>
          </div>
          <div className="space-y-1.5">
            {STARTER_PROMPTS.map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => go("ask-ai")}
                className="w-full text-left text-sm text-zinc-300 hover:text-white bg-white/[0.03] hover:bg-white/[0.06] border border-white/10 hover:border-amber-500/30 rounded-lg px-3 py-2 transition-colors flex items-center justify-between gap-2 group"
                data-testid={`home-ai-prompt-${p.slice(0,15).replace(/[^a-z0-9]+/gi,"-").toLowerCase()}`}
              >
                <span>{p}</span>
                <ArrowRight className="w-3.5 h-3.5 text-zinc-500 group-hover:text-amber-300 group-hover:translate-x-0.5 transition-all flex-shrink-0" />
              </button>
            ))}
          </div>
        </div>
      </section>

      {videoOpen && <DemoVideoModal onClose={() => setVideoOpen(false)} />}
    </div>
  );
}
